import { useEffect, useRef, useState, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import api from '../../api';

const TASK_BANNER = {
    chatting:            { text: '見積書を受け取るまで業者とやり取りしましょう', type: 'info' },
    estimated:           { text: '見積書が届きました', type: 'action', action: 'estimate', actionLabel: '見積書を確認する' },
    decided:             { text: '受注確定！業者から請求書が届くまでお待ちください', type: 'info' },
    invoiced:            { text: '請求書が届きました', type: 'action', action: 'invoice', actionLabel: '請求書を確認する' },
    paid:                { text: '支払い確認済み。工事日程の連絡をお待ちください', type: 'info' },
    scheduled:           { text: '工事日程が確定しました。工事開始をお待ちください', type: 'info' },
    in_progress:         { text: '工事中です。完了通知をお待ちください', type: 'info' },
    completion_notified: { text: '工事が完了しました', type: 'action', action: 'completion', actionLabel: '仕上がりを確認する' },
    completed:           { text: 'すべての手続きが完了しました。ありがとうございました！', type: 'done' },
};

const INNER = { maxWidth: 800, margin: '0 auto', padding: '0 20px' };

// ── アイコン ──────────────────────────────────────────────────────
const FileTextIcon = () => (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#f59e0b" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0 }}>
        <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
        <polyline points="14 2 14 8 20 8" />
        <line x1="16" y1="13" x2="8" y2="13" /><line x1="16" y1="17" x2="8" y2="17" />
    </svg>
);
const InfoIcon = () => (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0 }}>
        <circle cx="12" cy="12" r="10" /><line x1="12" y1="16" x2="12" y2="12" /><line x1="12" y1="8" x2="12.01" y2="8" />
    </svg>
);
const SendIcon = () => (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
        <path d="M22 2L11 13" stroke="#fff" strokeWidth="2" strokeLinecap="round" />
        <path d="M22 2L15 22l-4-9-9-4 20-7z" stroke="#fff" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
);
const ArrowRightIcon = () => (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <line x1="5" y1="12" x2="19" y2="12" /><polyline points="12 5 19 12 12 19" />
    </svg>
);

// ── ドキュメントカード ─────────────────────────────────────────────
const DocumentCard = ({ type, projectId, navigate }) => {
    const isEstimate = type === 'estimate';
    return (
        <div
            onClick={() => navigate(isEstimate ? `/customer/estimate/${projectId}` : `/customer/invoice/${projectId}`)}
            style={{ background: '#fff', border: '1px solid #e0e4ea', borderRadius: 10, padding: '12px 16px', maxWidth: 240, cursor: 'pointer', boxShadow: '0 1px 4px rgba(0,0,0,0.08)' }}
        >
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#6b7280" strokeWidth="1.8">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M19.5 14.25v-2.625a3.375 3.375 0 00-3.375-3.375h-1.5A1.125 1.125 0 0113.5 7.125v-1.5a3.375 3.375 0 00-3.375-3.375H8.25m0 12.75h7.5m-7.5 3H12M10.5 2.25H5.625c-.621 0-1.125.504-1.125 1.125v17.25c0 .621.504 1.125 1.125 1.125h12.75c.621 0 1.125-.504 1.125-1.125V11.25a9 9 0 00-9-9z" />
                </svg>
                <strong style={{ fontSize: '0.88rem', color: '#1a1a2e' }}>{isEstimate ? '見積書' : '請求書'}</strong>
            </div>
            <p style={{ fontSize: '0.78rem', color: '#6b7280', margin: 0 }}>{isEstimate ? '見積書が届きました' : '請求書が届きました'}</p>
            <p style={{ fontSize: '0.78rem', color: '#005c53', margin: '4px 0 0', fontWeight: 600 }}>タップして確認する →</p>
        </div>
    );
};

// ── バブル ────────────────────────────────────────────────────────
const Bubble = ({ msg, isMe }) => {
    const time = msg.created_at
        ? new Date(msg.created_at).toLocaleTimeString('ja-JP', { hour: '2-digit', minute: '2-digit' })
        : '';
    return (
        <div style={{ display: 'flex', justifyContent: isMe ? 'flex-end' : 'flex-start', marginBottom: 10 }}>
            <div style={{ maxWidth: '72%', display: 'flex', flexDirection: 'column', alignItems: isMe ? 'flex-end' : 'flex-start' }}>
                {!isMe && <div style={{ fontSize: '0.72rem', color: '#9ca3af', marginBottom: 3, marginLeft: 4 }}>業者</div>}
                <div style={{
                    background: isMe ? '#005c53' : '#fff',
                    color: isMe ? '#fff' : '#1a1a2e',
                    borderRadius: isMe ? '16px 0 16px 16px' : '0 16px 16px 16px',
                    padding: '10px 14px', fontSize: '0.9rem',
                    border: isMe ? 'none' : '1px solid #e0e4ea',
                    boxShadow: '0 1px 3px rgba(0,0,0,0.06)',
                    lineHeight: 1.55, whiteSpace: 'pre-wrap',
                }}>
                    {msg.content}
                </div>
                {time && <div style={{ fontSize: '0.68rem', color: '#9ca3af', marginTop: 3, [isMe ? 'marginRight' : 'marginLeft']: 4 }}>{time}</div>}
            </div>
        </div>
    );
};

// ── バナー（カード部分のみ） ───────────────────────────────────────
const TaskBannerCard = ({ banner, projectId, navigate }) => {
    if (!banner) return null;
    const isAction = banner.type === 'action';
    const isDone = banner.type === 'done';
    const bg = isAction ? '#fff8e1' : isDone ? '#e8f5e9' : '#f0f9ff';
    const border = isAction ? '#fbbf24' : isDone ? '#81c784' : '#90caf9';
    const textColor = isAction ? '#92400e' : isDone ? '#1b5e20' : '#1e40af';
    const iconColor = isAction ? '#f59e0b' : isDone ? '#2e7d32' : '#3b82f6';

    const handleAction = () => {
        if (banner.action === 'estimate') navigate(`/customer/estimate/${projectId}`);
        else if (banner.action === 'invoice') navigate(`/customer/invoice/${projectId}`);
        else if (banner.action === 'completion') navigate(`/customer/completion/${projectId}`);
    };

    return (
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', background: bg, border: `1px solid ${border}`, borderRadius: 8, padding: '10px 14px', gap: 10 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, flex: 1, minWidth: 0 }}>
                <span style={{ color: iconColor }}>{isAction ? <FileTextIcon /> : <InfoIcon />}</span>
                <span style={{ fontSize: '0.84rem', fontWeight: 500, color: textColor }}>{banner.text}</span>
            </div>
            {isAction && (
                <button onClick={handleAction} style={{ padding: '6px 12px', background: '#005c53', color: '#fff', border: 'none', borderRadius: 6, fontSize: '0.78rem', fontWeight: 600, cursor: 'pointer', flexShrink: 0, whiteSpace: 'nowrap' }}>
                    {banner.actionLabel}
                </button>
            )}
        </div>
    );
};

// ── メイン ────────────────────────────────────────────────────────
const ProjectChatPage = () => {
    const { projectId } = useParams();
    const navigate = useNavigate();
    const customerId = parseInt(localStorage.getItem('customer_id') || '0', 10);

    const [project, setProject] = useState(null);
    const [messages, setMessages] = useState([]);
    const [text, setText] = useState('');
    const [sending, setSending] = useState(false);
    const bottomRef = useRef(null);
    const lastMsgCount = useRef(0);
    const textareaRef = useRef(null);

    useEffect(() => {
        const el = textareaRef.current;
        if (!el) return;
        el.style.height = 'auto';
        el.style.height = `${Math.min(el.scrollHeight, 160)}px`;
    }, [text]);

    const fetchProject = useCallback(() => {
        api.get(`projects/${projectId}`).then(r => setProject(r.data)).catch(console.error);
    }, [projectId]);

    useEffect(() => {
        fetchProject();
        const t = setInterval(fetchProject, 5000);
        return () => clearInterval(t);
    }, [fetchProject]);

    const fetchMessages = useCallback(() => {
        api.get(`messages/by_project/${projectId}`).then(r => setMessages(r.data)).catch(console.error);
    }, [projectId]);

    useEffect(() => {
        fetchMessages();
        const t = setInterval(fetchMessages, 3000);
        return () => clearInterval(t);
    }, [fetchMessages]);

    useEffect(() => {
        if (messages.length > lastMsgCount.current) {
            bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
            lastMsgCount.current = messages.length;
        }
    }, [messages]);

    const sendMessage = async () => {
        if (!text.trim() || sending) return;
        setSending(true);
        try {
            await api.post('messages', { project_id: Number(projectId), content: text.trim(), user_type: 'customer', user_id: customerId });
            setText('');
            if (textareaRef.current) textareaRef.current.style.height = 'auto';
            fetchMessages();
        } catch (e) { console.error(e); }
        finally { setSending(false); }
    };

    const handleKeyDown = (e) => {
        if (e.key === 'Enter' && e.shiftKey && !e.nativeEvent.isComposing) { e.preventDefault(); sendMessage(); }
    };

    if (!project) return (
        <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100dvh' }}>
            <div style={{ width: 32, height: 32, border: '3px solid #e0e4ea', borderTopColor: '#005c53', borderRadius: '50%', animation: 'spin 0.8s linear infinite' }} />
            <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
        </div>
    );

    const status = project.status;
    const banner = TASK_BANNER[status];
    const contractorName = project.contractor?.company_name || '業者';
    const isDecided = ['decided', 'invoiced', 'paid', 'scheduled', 'in_progress', 'completion_notified', 'completed'].includes(status);
    const hasEstimate = ['estimated', 'decided', 'invoiced', 'paid', 'scheduled', 'in_progress', 'completion_notified', 'completed'].includes(status);
    const hasInvoice = ['invoiced', 'paid', 'scheduled', 'in_progress', 'completion_notified', 'completed'].includes(status);

    return (
        <div style={{ position: 'fixed', inset: 0, zIndex: 100, display: 'flex', flexDirection: 'column', background: '#f5f6f8' }}>

            {/* ヘッダー — 全幅、内側 max-width */}
            <div style={{ background: '#005c53', flexShrink: 0 }}>
                <div style={{ ...INNER, display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '12px 20px' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                        <button onClick={() => navigate(-1)} style={{ background: 'none', border: 'none', color: '#fff', padding: 0, fontSize: '1.3rem', cursor: 'pointer', lineHeight: 1 }}>‹</button>
                        <button
                            onClick={() => project.contractor_id && navigate(`/customer/contractor/${project.contractor_id}`)}
                            style={{ background: 'none', border: 'none', color: '#fff', padding: 0, cursor: 'pointer', textAlign: 'left' }}
                        >
                            <div style={{ fontWeight: 700, fontSize: '1rem' }}>{contractorName}</div>
                            <div style={{ fontSize: '0.72rem', color: 'rgba(255,255,255,0.65)', display: 'flex', alignItems: 'center', gap: 3 }}>
                                業者情報を見る <ArrowRightIcon />
                            </div>
                        </button>
                    </div>
                    <span style={{ background: 'rgba(255,255,255,0.2)', borderRadius: 20, padding: '4px 12px', fontSize: '0.78rem', fontWeight: 600, color: '#fff' }}>
                        {isDecided ? '契約中' : hasEstimate ? '見積届済' : '見積書待ち'}
                    </span>
                </div>
            </div>

            {/* メッセージエリア — スクロール、内側 max-width */}
            <div style={{ flex: 1, overflowY: 'auto' }}>
                <div style={{ ...INNER, padding: '24px 20px', display: 'flex', flexDirection: 'column' }}>
                    {hasEstimate && (
                        <div style={{ display: 'flex', justifyContent: 'flex-start', marginBottom: 12 }}>
                            <DocumentCard type="estimate" projectId={projectId} navigate={navigate} />
                        </div>
                    )}
                    {hasInvoice && (
                        <div style={{ display: 'flex', justifyContent: 'flex-start', marginBottom: 12 }}>
                            <DocumentCard type="invoice" projectId={projectId} navigate={navigate} />
                        </div>
                    )}
                    {messages.length === 0 && (
                        <div style={{ textAlign: 'center', color: '#9ca3af', fontSize: '0.85rem', marginTop: 40 }}>
                            まだメッセージはありません。<br />業者に挨拶してみましょう！
                        </div>
                    )}
                    {messages.map((msg, i) => (
                        <Bubble key={msg.id || i} msg={msg} isMe={msg.user_type === 'customer'} />
                    ))}
                    <div ref={bottomRef} />
                </div>
            </div>

            {/* バナー — グレー背景上、入力欄直上 */}
            {banner && (
                <div style={{ flexShrink: 0 }}>
                    <div style={{ ...INNER, padding: '0 20px 10px' }}>
                        <TaskBannerCard banner={banner} projectId={projectId} navigate={navigate} />
                    </div>
                </div>
            )}

            {/* 入力エリア — 全幅、内側 max-width */}
            <div style={{ background: '#fff', borderTop: '1px solid #e0e4ea', flexShrink: 0 }}>
                <div style={{ ...INNER, padding: '10px 20px', display: 'flex', gap: 8, alignItems: 'flex-end' }}>
                    <textarea
                        ref={textareaRef}
                        rows={1}
                        placeholder="メッセージを入力… (Shift+Enterで送信)"
                        value={text}
                        onChange={e => setText(e.target.value)}
                        onKeyDown={handleKeyDown}
                        style={{ flex: 1, resize: 'none', padding: '10px 14px', fontSize: '0.9rem', border: '1px solid #e0e4ea', borderRadius: 8, outline: 'none', lineHeight: 1.5, overflowY: 'auto', minHeight: 44, maxHeight: 160, boxSizing: 'border-box' }}
                    />
                    <button
                        onClick={sendMessage}
                        disabled={!text.trim() || sending}
                        style={{ width: 44, height: 44, borderRadius: 8, background: text.trim() ? '#005c53' : '#e0e4ea', border: 'none', cursor: text.trim() ? 'pointer' : 'default', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, transition: 'background 0.15s' }}
                    >
                        {sending
                            ? <div style={{ width: 18, height: 18, border: '2px solid rgba(255,255,255,0.4)', borderTopColor: '#fff', borderRadius: '50%', animation: 'spin 0.8s linear infinite' }} />
                            : <SendIcon />}
                    </button>
                </div>
            </div>

            <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
        </div>
    );
};

export default ProjectChatPage;
